// Edge: open blank tab before await; embed blob PDF in an iframe (bare blob: nav is often blank).

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function parseFilenameFromContentDisposition(header) {
  if (!header) return null;
  const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^["']|["']$/g, ""));
    } catch (_e) {
      // fall through
    }
  }
  const plain = /filename\s*=\s*("?)([^";]+)\1/i.exec(header);
  return plain?.[2]?.trim() || null;
}

export function openBlankPdfTab() {
  const win = window.open("about:blank", "_blank");
  if (!win) return null;
  try {
    win.document.title = "Loading PDF…";
  } catch (_e) {
    // ignore
  }
  return win;
}

export function showPdfBlobInTab(win, blob, options = {}) {
  const filename = options.filename || "document.pdf";
  const pdfBlob = blob.type && blob.type.toLowerCase().includes("pdf")
    ? blob
    : new Blob([blob], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(pdfBlob);

  try {
    const safeTitle = escapeHtml(filename);
    win.document.open();
    win.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeTitle}</title>`
      + "<style>html,body{margin:0;height:100%;overflow:hidden}"
      + "iframe{border:0;width:100%;height:100%;display:block}</style></head>"
      + `<body><iframe src="${blobUrl}" title="${safeTitle}"></iframe></body></html>`
    );
    win.document.close();
  } catch (_e) {
    try {
      win.location.replace(blobUrl);
    } catch (_e2) {
      win.location.href = blobUrl;
    }
  }

  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
  return { ok: true };
}

export function downloadPdfBlob(blob, filename = "document.pdf") {
  const pdfBlob = blob.type && blob.type.toLowerCase().includes("pdf")
    ? blob
    : new Blob([blob], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  return { ok: true, downloaded: true };
}

export async function fetchAndOpenPdf(url, options = {}) {
  const win = openBlankPdfTab();
  if (!win) {
    return { error: "Pop-up blocked. Allow pop-ups for this site to view the PDF." };
  }

  const closeWin = () => {
    try {
      win.close();
    } catch (_e) {
      // ignore
    }
  };

  try {
    const res = await fetch(url, {
      method: "GET",
      headers: options.headers || {},
      credentials: options.credentials || "same-origin",
    });

    if (!res.ok) {
      const raw = await res.text();
      let detail = raw?.slice(0, 300) || res.statusText || `Request failed (${res.status})`;
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) detail = parsed.join(" ");
        else if (parsed && typeof parsed === "object" && parsed.errors) {
          detail = Array.isArray(parsed.errors) ? parsed.errors.join(" ") : String(parsed.errors);
        }
      } catch (_e) {
        // keep detail
      }
      closeWin();
      return { error: detail };
    }

    const buf = await res.arrayBuffer();
    const mime = res.headers.get("Content-Type")?.split(";")[0]?.trim() || "";
    const blob = new Blob([buf], {
      type: mime.toLowerCase().includes("pdf") ? "application/pdf" : mime || "application/pdf",
    });
    const filename =
      parseFilenameFromContentDisposition(res.headers.get("Content-Disposition"))
      || options.defaultFilename
      || "document.pdf";

    return showPdfBlobInTab(win, blob, { filename });
  } catch (e) {
    closeWin();
    return { error: e?.message || "Failed to open PDF." };
  }
}
